import {Device} from '@fantasmagames/core/src/device/Device';
import {IPreloader, Preloader} from '@fantasmagames/core/src/loader/Preloader';
import {WebGlPerformanceCaveatResult} from '@fantasmagames/core/src/System';
import {ViewTypes} from '@fantasmagames/slot/src/common/view/View';
import {injectable} from 'inversify';
import {INineSliceTextureAndBorders} from '@fantasmagames/core/src/display/NineSlicePlane';

export interface TexturesMap {
	[id: string]: PIXI.Texture;
}

export interface SpinesMap {
	[id: string]: PIXI.spine.core.SkeletonData;
}

export interface SpritesheetMap {
	[id: string]: PIXI.Spritesheet;
}

export interface SoundMap {
	[name: string]: PIXI.sound.Sound;
}

export interface SoundVolumeMap {
	[name: string]: number;
}

export interface StyleSheetMap {
	[name: string]: any;
}

export interface HelpFilesMap {
	[name: string]: any;
}

export interface ConfigFilesMap {
	[name: string]: any;
}

export interface ParticleEmitterData {
	[id: string]: any;
}

export interface NineSliceData {
	[id: string]: {
		ninesliceborders: NineSliceBorders;
	};
}

export interface NineSliceBorders {
	leftWidth: number;
	topHeight: number;
	rightWidth: number;
	bottomHeight: number;
}

// export interface NineSliceBorders {

// }

export enum Platform {
	DESKTOP = 0,
	MOBILE = 1,
}

export interface JSONStringData {
	[name: string]: object | string;
}

@injectable()
export class ResourcesLoader {
	protected _platform: Platform;

	protected _majorPerformanceCaveat: boolean = false;

	protected _supportedLocales: string[] = ['en'];
	protected shouldRemoveUnsupportedLocales: boolean = false;

	protected _mobileSoundResourcesFiles: string[] = [];
	protected _mobileSoundDataFile: string = '';
	protected _soundResources: string[] = [];
	protected _particleResources: string[] = [];
	protected _translationResources: string[] = [];
	protected _resources: string[] = [];
	protected _localeTexts: string[] = [];
	protected _currencyResources: string[] = [];
	protected _helpFiles: string[] = [];
	protected _helpStyles: string[] = [];
	protected _spineResources: string[] = [];
	protected _configFiles: string[] = [];

	protected _languageData: JSONStringData;
	protected _englishLanguageData: JSONStringData;
	protected _locale: string;
	protected _localeTextData: any;
	protected _localeTextImage: any;
	protected _englishLocaleTextData: any;
	protected _englishLocaleTextImage: any;

	protected _helpFilesMap: HelpFilesMap = {};
	protected _configFilesMap: ConfigFilesMap = {};
	protected _styleSheets: StyleSheetMap = {};
	protected _textures: TexturesMap = {};
	protected _spines: SpinesMap = {};
	protected _spritesheets: SpritesheetMap = {};
	protected _sounds: SoundMap = {};
	protected _soundConfig: any = {};

	protected _nineslicedata: NineSliceData = {};

	protected _particleData: ParticleEmitterData = {};

	protected _loader: PIXI.loaders.Loader;
	protected _resourcesLoaded: number = -1;
	protected _soundsLoaded: number = -1;
	protected _soundConfigsLoaded: number = -1;
	protected _particlesLoaded: number = -1;
	protected _translationsLoaded: number = -1;
	protected _localeTextsLoaded: number = -1;
	protected _atlasLoaded: number = -1;
	protected _helpFilesLoaded: number = -1;
	protected _helpStylesLoaded: number = -1;
	protected _currenciesLoaded: string[] = [];
	protected _currenciesLoading: string[] = [];
	protected _configFilesLoaded: number = -1;
	// protected _atlasResourcesLoaded: number;

	protected _expectedToLoad: number;
	protected _soundsExpectedToLoad: number;
	protected _soundConfigsExpectedToLoad: number;
	protected _particlesExpectedToLoad: number;
	protected _translationsExpectedToLoad: number;
	protected _localeTextsExpectedToLoad: number;
	protected _atlasExpectedToLoad: number;
	protected _helpStylesExpectedToLoad: number;
	protected _helpFilesExpectedToLoad: number;
	protected _configFilesExpectedToLoad: number;
	protected _atlasResourcesExpectedToLoad: number;
	protected _currenciesExpectedToLoad: number;

	protected _loadingCompletedCallback: Function;
	protected _soundsLoadingCompletedCallback: Function;
	protected _soundConfigLoadingCompletedCallback: Function;
	protected _particleLoadingCompletedCallback: Function;
	protected _translationLoadingCompletedCallback: Function;
	protected _localeTextLoadingCompletedCallback: Function;
	protected _currenciesLoadingCompletedCallback: Function;
	protected _helpStylesLoadingCompletedCallback: Function;
	protected _helpFilesLoadingCompletedCallback: Function;
	protected _configFilesLoadingCompletedCallback: Function;

	protected _languageIndex: number;
	protected _localeTextIndex: number;
	protected _getUrlPath: Function;

	protected _device: Device;

	protected _currentLoadingQueueIndex: number = -1;
	protected _expectedToLoadInThisQueue: number = 0;
	protected _preloader: IPreloader;

	protected _useSingleSpineAtlas: boolean = false;

	// tODO: Decide variable type.
	protected _spineAtlasFile: any;
	// auto-populate? How to solve this depending on amount of assets?
	protected _atlasImages: string[] = [];
	protected _atlasMobileImages: string[] = [];
	protected _atlasCompletedCallback: Function;
	protected _spineAtlasData: any;
	protected _spineAtlasImages: PIXI.loaders.Resource[] = [];

	protected _spineAtlasLoader: PIXI.spine.core.AtlasAttachmentLoader;
	protected _spineJsonParser: PIXI.spine.core.SkeletonJson;
	protected _maxDownloadStreams: number = 8;

	protected _loadingLandscapeAndPortraitResources: boolean = false;

	constructor(device: Device, getUrlPath: Function = null, locale: string = 'EN', majorCaveatPerformance: WebGlPerformanceCaveatResult) {
		this._device = device;
		this._getUrlPath = getUrlPath;
		this._majorPerformanceCaveat = majorCaveatPerformance === 0 ? true : false;

		this._locale = locale;

		if (__PRODUCTION__) {
			this._useSingleSpineAtlas = true;
		}

		this.initialize();
		this.addPlatformSpecificArt();
		this.addPreloader();
	}

	public static isRecode(): boolean {
		return (window as any).V;
	}

	public endLoading(): void {
		this._preloader.endLoading();
	}

	public load(loadingCallback: Function, platform: Platform): void {
		this._platform = platform;
		this._loadingCompletedCallback = loadingCallback;
		if (this._device.isMobile && this._loadingLandscapeAndPortraitResources) {
			this._expectedToLoad = this._resources.length * 2;
		} else {
			this._expectedToLoad = this._resources.length;
		}
		this._expectedToLoad = this._resources.length;
		this._loader = this.createLoader();

		this.loadNextResource(null, null);
	}

	public calculateExpectedTotal(): void {
		this._particlesExpectedToLoad = this._particleResources.length;
		this._soundsExpectedToLoad = this._device.isMobile ? this._mobileSoundResourcesFiles.length : this._soundResources.length;
		this._soundConfigsExpectedToLoad = this._device.isMobile ? 1 : 0;

		// one file for GR and one for Paytable. In case the locale is something other than english we load two more.
		this._helpFilesExpectedToLoad = this._locale === 'en' ? 2 : 4;
		this._configFilesExpectedToLoad = this._configFiles.length;
		this._helpStylesExpectedToLoad = 1;
		this._translationsExpectedToLoad = this._locale === 'en' ? 1 : 2;
		this._localeTextsExpectedToLoad = this._locale === 'en' || this._localeTexts.indexOf(this._localeTextData) < 0 ? 1 : 2;
		this._atlasExpectedToLoad = 1 + this._atlasImages.length;

		this._expectedToLoad = this._resources.length;
	}

	public loadSounds(loadingCallback: Function): void {
		this._soundsLoadingCompletedCallback = loadingCallback;
		this._soundsExpectedToLoad = this._device.isMobile ? this._mobileSoundResourcesFiles.length : this._soundResources.length;
		this._loader = this.createLoader();

		this.loadNextSound(null);
	}

	public loadSoundsConfig(loadingCallback: Function): void {
		this._soundConfigLoadingCompletedCallback = loadingCallback;
		this._soundConfigsExpectedToLoad = this._device.isMobile ? 1 : 0;
		this._loader = this.createLoader();

		this.loadSoundConfig(null);
	}

	public loadSoundConfig(resource: any): void {
		if (resource && resource.data && resource.extension && resource.extension === 'json') {
			this._soundConfig = resource.data;
		}

		this._soundConfigsLoaded++;
		if (this._soundConfigsLoaded >= this._soundConfigsExpectedToLoad) {
			this._soundConfigLoadingCompletedCallback();

			return;
		}

		this._loader.reset();

		this._loader.add(this.getPathForResource(this._mobileSoundDataFile), {}, this.loadSoundConfig.bind(this));

		this._loader.load();
		this.updateProgress();
	}

	public loadParticles(loadingCallback: Function): void {
		this._particleLoadingCompletedCallback = loadingCallback;
		this._particlesExpectedToLoad = this._particleResources.length;
		this._loader = this.createLoader();

		this.loadNextParticle(null);
	}

	public loadConfigFiles(loadingCallback: Function): void {
		this._configFilesLoadingCompletedCallback = loadingCallback;
		this._loader = this.createLoader();
		this.loadNextConfigFile(null);
	}

	protected loadNextConfigFile(resource: any): void {
		if (this._configFilesLoaded >= 0) {
			if (resource.error === null && resource.url && resource.data !== null) {
				const name: string = resource.url;
				this._configFilesMap[name.substring(name.lastIndexOf('/') + 1, name.length)] = resource.data;
			}
		}

		this._configFilesLoaded++;
		if (this._configFilesLoaded >= this._configFilesExpectedToLoad) {
			this._configFilesLoadingCompletedCallback();

			return;
		}
		this._loader.reset();
		this._loader.add(this.getPathForResource(this._configFiles[this._configFilesLoaded]), {}, this.loadNextConfigFile.bind(this));

		this._loader.load();
		this.updateProgress();
	}

	public loadHelpFiles(loadingCallback: Function, locale?: string): void {
		this._helpFilesLoadingCompletedCallback = loadingCallback;
		// tODO: Add to function.
		this._helpFilesExpectedToLoad = 2; // needs to load english at least?
		// add correct locale files.

		// tODO: Check from list of available translations.
		if (this._locale !== 'en') {
			this._helpFilesExpectedToLoad += 2;
			this._helpFiles = this._helpFiles.concat([
				`HTML/PAYTABLE/Paytable_${this._locale}.htm`,
				`HTML/GAMERULES/Gamerules_${this._locale}.htm`,
			]);
		}
		this._loader = this.createLoader();

		this.loadNextHelpFile(null);
	}

	public loadNextHelpFile(resource: any): void {
		if (this._helpFilesLoaded >= 0) {
			if (resource.error === null && resource.url && resource.data !== null && resource.xhr && resource.xhr.responseText) {
				const bodyRegex = /(<body)(.|\r|\n)*(<\/body>)/gm;
				const responseText: string = resource.xhr.responseText;

				const bodyHTMLText: string = responseText.match(bodyRegex)[0];
				const helpfileItem: string = resource.url
					.toLowerCase()
					.split(`resources/${this._device.isMobile ? 'mobile' : 'desktop'}/`)[1];

				this._helpFilesMap[helpfileItem] = bodyHTMLText;
			}
		}

		this._helpFilesLoaded++;
		if (this._helpFilesLoaded >= this._helpFilesExpectedToLoad) {
			this._helpFilesLoadingCompletedCallback();

			return;
		}
		this._loader.reset();
		this._loader.add(this.getDevicePathForResource(this._helpFiles[this._helpFilesLoaded]), {}, this.loadNextHelpFile.bind(this));
		// this._helpFiles[this._helpFilesLoaded], {}, this.loadNextHelpFile.bind(this);

		this._loader.load();
		this.updateProgress();
	}

	public loadHelpStyles(loadingCallback: Function): void {
		this._helpStylesLoadingCompletedCallback = loadingCallback;
		this._helpStylesExpectedToLoad = 1;
		this._loader = this.createLoader();

		this.loadNextHelpStyle(null);
	}

	public loadNextHelpStyle(resource: any): void {
		if (this._helpStylesLoaded >= 0) {
			// checking for errors.
			if (resource.url && resource.error === null) {
				// tODO: Temp, replace with real getttttter for device and path.
				this._styleSheets[resource.url.split(`resources/${this._device.isMobile ? 'Mobile' : 'Desktop'}/`)[1]] = resource.data;
			}
		}

		this._helpStylesLoaded++;
		if (this._helpStylesLoaded >= this._helpStylesExpectedToLoad) {
			this._helpStylesLoadingCompletedCallback();

			return;
		}
		this._loader.reset();
		this._loader.add(this.getDevicePathForResource(this._helpStyles[this._helpStylesLoaded]), {}, this.loadNextHelpStyle.bind(this));

		this._loader.load();
		this.updateProgress();
	}

	public addCurrencyToResourcesList(loadingCallback: Function, currencyISO: string = 'EUR'): void {
		const currencyResourceIndex = this._currencyResources.indexOf(`CURRENCIES/${currencyISO.toUpperCase()}.json`);

		if (currencyResourceIndex !== -1) {
			this._resources.push(this._currencyResources[currencyResourceIndex]);
		}

		loadingCallback();
	}

	public loadTranslations(loadingCallback: Function, locale?: string): void {
		this._translationLoadingCompletedCallback = loadingCallback;
		this._translationsExpectedToLoad = 1;

		this._loader = this.createLoader();

		if (this.shouldRemoveUnsupportedLocales) {
			this._locale = this.isSupportedLocale(locale) ? locale : 'en';
		} else {
			this._locale = locale;
		}
		const languageFile = `resources/languages/${this._locale}.json`;
		this._languageIndex = this._translationResources.indexOf(languageFile);

		if (this._languageIndex < 0) {
			this._locale = 'en';
			this._translationsExpectedToLoad = 1;
		} else {
			this._translationsExpectedToLoad += 1;
		}

		// makes it default to English which should be the first index of the resource-list.
		this._languageIndex = Math.max(0, this._languageIndex);
		this.loadTranslation(null);
	}

	public loadTranslation(resource: any): void {
		if (resource && resource.data && resource.data.hasOwnProperty('BaseStrings') && this._translationsLoaded === 0) {
			this._englishLanguageData = resource.data;
		} else if (resource && resource.data && resource.data.hasOwnProperty('BaseStrings')) {
			this._languageData = resource.data;
		}

		this._translationsLoaded++;
		if (this._translationsLoaded >= this._translationsExpectedToLoad) {
			this._translationLoadingCompletedCallback();

			return;
		}

		this._loader.reset();
		if (this._translationsLoaded > 0) {
			this._loader.add(this.getPathForResource(this._translationResources[this._languageIndex]), {}, this.loadTranslation.bind(this));
		} else {
			this._loader.add(this.getPathForResource(this._translationResources[0]), {}, this.loadTranslation.bind(this));
		}
		this._loader.load();

		this.updateProgress();
	}

	public loadSingleSpineAtlas(loadingCallback: Function): void {
		this._atlasCompletedCallback = loadingCallback;
		this._atlasExpectedToLoad = 1 + this._atlasImages.length;

		if (!this._useSingleSpineAtlas) {
			this._atlasLoaded = this._atlasExpectedToLoad;
			this.updateProgress();

			this._atlasCompletedCallback();

			return;
		}

		this._loader = this.createLoader();
		this.loadSpineAtlasResource(null);
	}

	public loadSpineAtlasResource(resource: any): void {
		if (resource && resource.data && resource.extension === 'atlas') {
			this._spineAtlasData = resource.data;
		} else if (resource && resource.data) {
			this._spineAtlasImages.push(resource);
		}

		this._atlasLoaded++;
		this._currentLoadingQueueIndex++;

		if (this._atlasLoaded >= this._atlasExpectedToLoad) {
			this._atlasCompletedCallback();

			return;
		}

		this.updateProgress();
		if (this._currentLoadingQueueIndex < this._expectedToLoadInThisQueue) {
			return;
		}

		this._loader.reset();

		let inProgressResources: number = 0;

		for (let i = 0; i < this._maxDownloadStreams; i++) {
			let atlasResource = this._atlasLoaded === 0 && i === 0 ? this._spineAtlasFile : this._atlasImages[this._atlasLoaded - 1 + i];

			if (atlasResource) {
				atlasResource = this.getDevicePathForResource(atlasResource);
				this._loader.add(atlasResource, {}, this.loadSpineAtlasResource.bind(this));
				inProgressResources++;
			}
		}

		this._currentLoadingQueueIndex = 0;
		this._expectedToLoadInThisQueue = inProgressResources;
		this._loader.load();
	}

	public loadLocaleTexts(loadingCallBack: Function, locale: string): void {
		let localeTextData = `LOCALE_TEXTS/${locale}.json`;
		this._localeTextIndex = this._localeTexts.indexOf(localeTextData);

		if (this._localeTextIndex < 0) {
			localeTextData = `LOCALE_TEXTS/en.json`;
			this._localeTextIndex = this._localeTexts.indexOf(localeTextData);
		}

		this._localeTextsExpectedToLoad = 1;
		this._localeTextLoadingCompletedCallback = loadingCallBack;
		this._loader = this.createLoader();

		this.loadLocaleText(null);
	}

	public loadLocaleText(resource: any): void {
		if (resource && resource.data && resource.data.hasOwnProperty('frames')) {
			this._localeTextData = resource;
			if (
				resource.hasOwnProperty('name') &&
				resource.name.indexOf('LOCALE_TEXTS/en.json') > -1 &&
				resource.hasOwnProperty('spritesheet')
			) {
				this._englishLocaleTextData = resource;
				this._resources.splice(this._resources.indexOf('LOCALE_TEXTS/en.json'), 1);
				this._expectedToLoad--;
			}
		}

		this._localeTextsLoaded++;
		if (this._localeTextsLoaded >= this._localeTextsExpectedToLoad) {
			this._localeTextLoadingCompletedCallback();

			return;
		}

		this._loader.reset();

		this._loader.add(
			this.getDevicePathForResource(this._localeTexts[this._localeTextIndex + this._localeTextsLoaded]),
			{},
			this.loadLocaleText.bind(this),
		);

		this._loader.load();

		this.updateProgress();
	}

	public loadNextParticle(resource: any): void {
		if (this._particlesLoaded >= 0) {
			if (resource.data.hasOwnProperty('maxParticles')) {
				this._particleData[this._particleResources[this._particlesLoaded]] = resource.data;
			} else if (resource.hasOwnProperty('spritesheet')) {
				this._spritesheets[resource.name] = resource.spritesheet;
			} else {
				this._textures[resource] = resource.texture;
			}
		}

		this._particlesLoaded++;
		if (this._particlesLoaded >= this._particlesExpectedToLoad) {
			this._particleLoadingCompletedCallback();

			return;
		}

		this._loader.reset();
		this._loader.add(this.getPathForResource(this._particleResources[this._particlesLoaded]), {}, this.loadNextParticle.bind(this));
		this._loader.load();

		this.updateProgress();
	}

	public getDevicePathForResource(resName: string): string {
		const platformPath: string = this._device.isMobile ? 'resources/Mobile/' : 'resources/Desktop/';

		return this._getUrlPath(platformPath + resName);
	}

	public getPathForResource(resName: string): string {
		return this._getUrlPath(resName);
	}

	public getTexture(id: string, viewType: ViewTypes = ViewTypes.DESKTOP): PIXI.Texture {
		if (viewType === ViewTypes.PORTRAIT) {
			id = 'portrait_' + id;
		}
		if (viewType === ViewTypes.LANDSCAPE) {
			id = 'landscape_' + id;
		}
		// tODO: remove temporary
		if (!this.existsBoolean(this._textures, id)) {
			id = 'landscape_' + id;
		}
		this.exists(this._textures, id);

		return this._textures[id];
	}

	public getHelpFilesData(id: string): any {
		this.exists(this._helpFilesMap, id);

		return this._helpFilesMap[id];
	}

	public getSpine(id: string, viewType: ViewTypes = ViewTypes.DESKTOP): PIXI.spine.core.SkeletonData {
		if (viewType === ViewTypes.PORTRAIT) {
			id = 'portrait_' + id;
		}
		if (viewType === ViewTypes.LANDSCAPE) {
			id = 'landscape_' + id;
		}

		this.exists(this._spines, id);

		return this._spines[id];
	}

	public hasSpine(id: string): boolean {
		return this.existsBoolean(this._spines, id);
	}

	public getNineSliceObject(sheetName: string, id: string): INineSliceTextureAndBorders {
		return {texture: this.getSpritesheetTexture(sheetName, id), nine: this.getNineSliceData(sheetName, id)};
	}

	public getSpritesheetTexture(sheetName: string, id: string, viewType: ViewTypes = ViewTypes.DESKTOP): PIXI.Texture {
		if (viewType === ViewTypes.PORTRAIT) {
			sheetName = 'portrait_' + sheetName;
		}
		if (viewType === ViewTypes.LANDSCAPE) {
			sheetName = 'landscape_' + sheetName;
		}
		// tODO: remove temporary
		if (!this.existsBoolean(this._spritesheets, sheetName)) {
			sheetName = 'landscape_' + sheetName;
		}
		this.exists(this._spritesheets, sheetName);
		this.exists(this._spritesheets[sheetName].textures, id);

		return this._spritesheets[sheetName].textures[id];
	}

	public getCurrencySpritesheetTexture(currencyISO: string, id: string): PIXI.Texture {
		const sheetName: string = `CURRENCIES/${currencyISO}.json`;

		if (this.existsBoolean(this._spritesheets, sheetName)) {
			this.existsBoolean(this._spritesheets[sheetName].textures, id);

			return this._spritesheets[sheetName].textures[id];
		} else {
			try {
				this.loadCurrencyISOResource(currencyISO);
			} catch (error) {
				// console.error(error);
			}

			return PIXI.Texture.EMPTY;
		}
	}

	public getSpriteSheet(sheetName: string): any {
		return this._spritesheets[sheetName];
	}

	public getTranslation(): any {
		return this._languageData;
	}

	public getEnglishStrings(): any {
		return this._englishLanguageData;
	}

	public getLocale(): string {
		return this._locale;
	}

	public getMobileSoundConfig(): any {
		return this._soundConfig;
	}

	public getConfig(configFileName: string): any {
		return this._configFilesMap[configFileName];
	}

	public getPaytableHTML(): string {
		return this._helpFilesMap.hasOwnProperty(`html/paytable/paytable_${this.getLocale()}.htm`)
			? this._helpFilesMap[`html/paytable/paytable_${this.getLocale()}.htm`]
			: this._helpFilesMap['html/paytable/paytable_en.htm'];
	}

	public getGameRulesHTML(): string {
		return this._helpFilesMap.hasOwnProperty(`html/gamerules/gamerules_${this.getLocale()}.htm`)
			? this._helpFilesMap[`html/gamerules/gamerules_${this.getLocale()}.htm`]
			: this._helpFilesMap[`html/gamerules/gamerules_en.htm`];
	}

	public getLocaleTextImage(id: string): PIXI.Texture {
		if (!this._localeTextData || !this._localeTextData.textures || !this._localeTextData.textures[id]) {
			// use for debug.
			if (!__PRODUCTION__) {
				throw new Error(`Cannot find texture:' ${id} in Locale Atlas.`);
			}

			// keep on Prod
			return this.getEnglishLocaleTextImage(id);
		}

		return this._localeTextData.textures[id];
	}

	public getEnglishLocaleTextImage(id: string): PIXI.Texture {
		if (!this._englishLocaleTextData.textures[id]) {
			return PIXI.Texture.EMPTY;
		}

		return this._englishLocaleTextData.textures[id];
	}

	public getParticleData(id: string): any {
		this._particleData[id].speed.start /= 1000;
		this._particleData[id].speed.end /= 1000;

		this._particleData[id].acceleration.x /= 1000000;
		this._particleData[id].acceleration.y /= 1000000;

		this._particleData[id].maxSpeed /= 1000;

		this._particleData[id].rotationSpeed.min /= 1000;
		this._particleData[id].rotationSpeed.max /= 1000;

		this._particleData[id].lifetime.min *= 1000;
		this._particleData[id].lifetime.max *= 1000;

		this._particleData[id].frequency *= 1000;
		this._particleData[id].emitterLifetime *= 1000;

		return this._particleData[id];
	}

	public getSound(id: string): PIXI.sound.Sound {
		this.exists(this._sounds, id);

		return this._sounds[id];
	}

	public getNineSliceData(atlasId: string, imageId: string): NineSliceBorders {
		if (this.exists(this._nineslicedata, atlasId)) {
			if (this.exists(this._nineslicedata[atlasId], imageId)) {
				return this._nineslicedata[atlasId][imageId].ninesliceborders;
			}
		}

		if (!__PRODUCTION__) {
			throw new Error(`Could not find resource: ${atlasId} / ${imageId}`);
		}
	}

	public usingUnsupportedDeviceOrBrowser(): void {
		this._preloader.usingUnsupportedDeviceOrBrowser();
	}

	protected initialize(): void {
		throw new Error('ReourcesLoader::initialize() must be overridden and some resources should be added');
	}

	protected addPlatformSpecificArt(): void {
		if (ResourcesLoader.isRecode()) {
			this.addMicrogamingArt();
		}
	}

	protected addPreloader(): void {
		this._preloader = new Preloader(this._getUrlPath, this._device);
		this._preloader.createLoadingScreen();
	}

	protected addMicrogamingArt(): void {
		// this._resources.push('FREE_GAMES_GENERIC_BUTTONS.json');
		this._resources.push('FREE-GAMES_SCREENS.json');
		this._resources.push('MICROGAMING.json');
	}

	protected createLoader(): PIXI.loaders.Loader {
		const loader: PIXI.loaders.Loader = new PIXI.loaders.Loader();

		loader.onError.add(this.loadingError.bind(this));

		return loader;
	}

	protected loadingError(error: string, loader: PIXI.loaders.Loader, resource: any): void {
		if (!__PRODUCTION__) {
			console.warn(error);
			console.warn(resource);
		}
	}

	protected loadNextSound(resource: any): void {
		if (this._soundsLoaded >= 0) {
			if (resource.hasOwnProperty('sound')) {
				// get the correct asset name.
				const soundName: string = resource.name.slice(resource.name.lastIndexOf('resources/'));
				this._sounds[soundName] = resource.sound;
			}
		}

		this._soundsLoaded++;
		this._currentLoadingQueueIndex++;
		if (this._soundsLoaded >= this._soundsExpectedToLoad) {
			this._soundsLoadingCompletedCallback();
			this.updateProgress();

			return;
		}

		this.updateProgress();
		if (this._currentLoadingQueueIndex < this._expectedToLoadInThisQueue) {
			return;
		}

		this._loader.reset();

		let inProgressResources: number = 0;

		for (let i = 0; i < this._maxDownloadStreams; i++) {
			const resourceName = this._device.isMobile
				? this._mobileSoundResourcesFiles[this._soundsLoaded + i]
				: this._soundResources[this._soundsLoaded + i];

			if (resourceName) {
				this._loader.add(this.getPathForResource(resourceName), {}, this.loadNextSound.bind(this));
				inProgressResources++;
			}
		}
		this._currentLoadingQueueIndex = 0;
		this._expectedToLoadInThisQueue = inProgressResources;

		this._loader.load();
	}

	public forceLoadCurrency(currencyISO: string, callback: Function): void {
		if (this._currenciesLoaded.indexOf(currencyISO) > -1) {
			callback && callback();

			return;
		}

		this._loader.reset();

		const currencyResourceName = this._currencyResources[this._currencyResources.indexOf(`CURRENCIES/${currencyISO}.json`)];
		this._loader.add(
			this.getDevicePathForResource(currencyResourceName),
			{},
			this.forcedLoadCurrencyResource.bind(this, callback, currencyISO, currencyResourceName),
		);

		this._loader.load();

	}

	protected forcedLoadCurrencyResource(callback: Function, currencyISO: string, resName: string, resource: any): void {
		if (resource.hasOwnProperty('spritesheet')) {
			this._spritesheets[resName] = resource.spritesheet;
		}

		this._currenciesLoading.splice(this._currenciesLoading.indexOf(currencyISO), 1);
		this._currenciesLoaded.push(currencyISO);

		this._loader.reset();

		callback && callback();
	}

	protected loadCurrencyISOResource(currencyISO: string): boolean {
		if (this._currenciesLoading.length > 0) {
			if (this._currenciesLoading.indexOf(currencyISO) > -1) {
				return true;
			}
		} else if (this._currenciesLoaded.indexOf(currencyISO) > -1) {
			return true;
		}

		this._loader.reset();

		this._currenciesLoading.push(currencyISO);

		const currencyResourceName = this._currencyResources[this._currencyResources.indexOf(`CURRENCIES/${currencyISO}.json`)];

		this._loader.add(
			this.getDevicePathForResource(currencyResourceName),
			{},
			this.loadCurrencyResource.bind(this, currencyISO, currencyResourceName),
		);

		this._loader.load();

		return false;
	}

	protected loadCurrencyResource(currencyISO: string, resName: string, resource: any): void {
		if (resource.hasOwnProperty('spritesheet')) {
			this._spritesheets[resName] = resource.spritesheet;
		}

		this._currenciesLoading.splice(this._currenciesLoading.indexOf(currencyISO), 1);
		this._currenciesLoaded.push(currencyISO);
		// console.log(`Loaded currency ISO:${currencyISO}`);

		this._loader.reset();
	}

	protected isSupportedLocale(locale: string): boolean {
		if (this._supportedLocales.indexOf(locale) > -1) {
			return true;
		}

		return false;
	}

	// imageloader for Spine files. References a single atlas file and it's .pngs instead of loading the ones with the same name as the .json.
	protected advancedImageLoader(loader: PIXI.loaders.Loader, namePrefix: string, baseUrl: string, imageOptions: any): Function {
		return function(line: string, callback: Function): void {
			const url = baseUrl + line;

			const textureResource = (imageOptions.metadata as PIXI.loaders.Resource[]).find((item: PIXI.loaders.Resource) => {
				return item.name === url;
			});

			callback(textureResource.texture.baseTexture);
		};
	}

	protected loadNextResource(resName: string, resource: any): void {
		const metaData = {};

		if (this._resourcesLoaded >= 0) {
			const resFullPath: string = resource.name;
			if (resFullPath.toLocaleLowerCase().indexOf('portrait') >= 0) {
				resName = `portrait_${resName}`;
			} else if (resFullPath.toLocaleLowerCase().indexOf('landscape') >= 0) {
				resName = `landscape_${resName}`;
			}
			if (resource.hasOwnProperty('spineData')) {
				this._spines[resName] = resource.spineData;
				if (!__PRODUCTION__) {
					this._spines[resName].name = resName;
				}
			} else if (
				resource.hasOwnProperty('name') &&
				resource.name.indexOf('LOCALE_TEXTS/en.json') > -1 &&
				resource.hasOwnProperty('spritesheet')
			) {
				this._englishLocaleTextData = resource.spritesheet;
			} else if (resource.hasOwnProperty('spritesheet')) {
				this._spritesheets[resName] = resource.spritesheet;
				if (resource.data.hasOwnProperty('nineslicedata')) {
					this._nineslicedata[resName] = resource.data.nineslicedata;
					if (!__PRODUCTION__) {
						console.log(resource.data.nineslicedata);
					}
				}
			} else {
				this._textures[resName] = resource.texture;
			}
		}

		this._resourcesLoaded++;
		this._currentLoadingQueueIndex++;
		if (this.isLoadingTwoBunchesOfResources()) {
			if (this._resourcesLoaded >= this._expectedToLoad * 2) {
				this._loadingCompletedCallback();
				this.updateProgress();

				return;
			}
		} else {
			if (this._resourcesLoaded >= this._expectedToLoad) {
				this._loadingCompletedCallback();
				this.updateProgress();

				return;
			}
		}

		this.updateProgress();
		if (this._currentLoadingQueueIndex < this._expectedToLoadInThisQueue) {
			return;
		}

		if (this.isLoadingTwoBunchesOfResources()) {
			this.loadBunchOfResourcesMobileLandscapePortrait(metaData);
		} else {
			this.loadBunchOfResources(metaData);
		}
	}

	protected isLoadingTwoBunchesOfResources(): boolean {
		return this._device.isMobile && this._loadingLandscapeAndPortraitResources;
	}

	protected loadBunchOfResources(metaData: any): void {
		this._loader.reset();
		let inProgressResources: number = 0;
		for (let i = 0; i < this._maxDownloadStreams / 2; i++) {
			if (this._resources[this._resourcesLoaded + i]) {
				if (this._useSingleSpineAtlas && this.isSpineResource(this._resources[this._resourcesLoaded + i])) {
					this._loader.add(
						this.getDevicePathForResource(this._resources[this._resourcesLoaded + i]),
						{
							metadata: {
								imageLoader: this.advancedImageLoader,
								imageMetadata: this._spineAtlasImages,
								atlasRawData: this._spineAtlasData,
							},
						},
						this.loadNextResource.bind(this, this._resources[this._resourcesLoaded + i]),
					);
				} else {
					this._loader.add(
						this.getDevicePathForResource(this._resources[this._resourcesLoaded + i]),
						{},
						this.loadNextResource.bind(this, this._resources[this._resourcesLoaded + i]),
					);
				}
				inProgressResources++;
			}
		}

		this._currentLoadingQueueIndex = 0;
		this._expectedToLoadInThisQueue = inProgressResources;

		this._loader.load();
	}

	protected loadBunchOfResourcesMobileLandscapePortrait(metaData: any): void {
		this._loader.reset();
		let inProgressResources: number = 0;
		for (let i = 0; i < this._maxDownloadStreams / 2; i++) {
			const index: number = this._resourcesLoaded / 2 + i;
			if (this._resources[index]) {
				this._loader.add(
					this.getPortraitDevicePathForResource(this._resources[index]),
					metaData,
					this.loadNextResource.bind(this, this._resources[index]),
				);
				inProgressResources++;

				this._loader.add(
					this.getLandscapeDevicePathForResource(this._resources[index]),
					metaData,
					this.loadNextResource.bind(this, this._resources[index]),
				);

				inProgressResources++;
			}
			if (!__PRODUCTION__) {
				console.log(`about to load ${this._resources[index]}`);
			}
		}

		this._currentLoadingQueueIndex = 0;
		this._expectedToLoadInThisQueue = inProgressResources;

		this._loader.load();
	}

	protected isSpineResource(name: string): boolean {
		return this._spineResources.indexOf(name) > -1;
	}

	protected getPortraitDevicePathForResource(resName: string): string {
		return this._getUrlPath('resources/Mobile/PORTRAIT/' + resName);
	}

	protected getLandscapeDevicePathForResource(resName: string): string {
		return this._getUrlPath('resources/Mobile/LANDSCAPE/' + resName);
	}

	protected getSpineAtlasAttachment(data: any): PIXI.spine.core.SkeletonData {
		// tODO: Hook up correctly and replace getSpine function.
		return this._spineJsonParser.readSkeletonData(data);
	}

	protected exists(arr: any, id: string): boolean {
		if (typeof arr === 'object') {
			if (arr.hasOwnProperty(id)) {
				return true;
			}
		}

		if (!__PRODUCTION__) {
			throw new Error(`Could not find resource: ${id}`);
		}

		return false;
	}

	protected existsBoolean(arr: any, id: string): boolean {
		if (typeof arr === 'object') {
			if (arr.hasOwnProperty(id)) {
				return true;
			}
		}

		return false;
	}

	protected updateProgress(): void {
		const totalItems =
			this._expectedToLoad +
			this._soundsExpectedToLoad +
			this._soundConfigsExpectedToLoad +
			this._translationsExpectedToLoad +
			this._localeTextsExpectedToLoad +
			this._atlasExpectedToLoad +
			this._helpStylesExpectedToLoad +
			this._helpFilesExpectedToLoad +
			this._configFilesExpectedToLoad +
			this._particlesExpectedToLoad;
		// this._atlasResourcesExpectedToLoad +

		const current =
			Math.max(0, this._resourcesLoaded) +
			Math.max(0, this._soundsLoaded) +
			Math.max(0, this._soundConfigsLoaded) +
			Math.max(0, this._helpFilesLoaded) +
			Math.max(0, this._translationsLoaded) +
			Math.max(0, this._localeTextsLoaded) +
			Math.max(0, this._helpStylesLoaded) +
			Math.max(0, this._atlasLoaded) +
			Math.max(0, this._configFilesLoaded) +
			Math.max(0, this._particlesLoaded);

		this._preloader.updateProgress(totalItems, current);
	}

	public get hasMajorPerformanceCaveat(): boolean {
		return this._majorPerformanceCaveat;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/core/src/loader/ResourcesLoader.ts